import type { HTMLAttributes, ReactNode } from 'react';

/** A kit chip look (packages/ui-kit/base.css `.bk-chip--*`): ok / bad the ink word, warn / quiet the grey word,
 *  on the ink fill (a selection), alert the red fill (an urgent word). No look = the plain ink word. */
export type ChipLook = 'ok' | 'warn' | 'bad' | 'quiet' | 'on' | 'alert';

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  look?: ChipLook;
  /** A count (`3`, a ratio `4/7`, a request's `#4`): the kit's `--count` form -- at least 2em, centred, tabular. */
  count?: number | string;
  /** A count of zero stays visible but recessed (`--zero`): "nothing here" is meaningful. */
  quietZero?: boolean;
  /** No text: the kit's `--dot` form, a small square in the look. Give it an aria-label, or aria-hidden. */
  dot?: boolean;
  /** The word (replaces the count's figures when both are given). */
  children?: ReactNode;
  className?: string;
  [data: `data-${string}`]: string | number | undefined;
}

const LOOK: Readonly<Record<ChipLook, string>> = {
  ok: 'bk-chip--ok',
  warn: 'bk-chip--warn',
  bad: 'bk-chip--bad',
  quiet: 'bk-chip--quiet',
  on: 'bk-chip--on',
  alert: 'bk-chip--alert',
};

/**
 * THE CHIP: the kit's status word (`.bk-chip`), a thin wrapper that emits ONLY kit classes -- a look modifier and
 * the kit's forms (`--count`, `--zero`, `--dot`, kit 0.4.1). pmt draws no chip CSS of its own. A review card state
 * is drawn by features/shared/chips/StateChip (its look and word), a role by RoleChip.
 */
export function Chip({ look, count, quietZero, dot, children, className = '', ...rest }: ChipProps) {
  const classes = [
    'bk-chip',
    look ? LOOK[look] : '',
    count !== undefined ? 'bk-chip--count' : '',
    dot ? 'bk-chip--dot' : '',
    count === 0 && quietZero ? 'bk-chip--zero' : '',
    className,
  ].filter(Boolean).join(' ');
  const text = dot ? null : children !== undefined ? children : count !== undefined ? String(count) : null;
  return (
    <span className={classes} {...rest}>
      {text}
    </span>
  );
}
